import React, { JSX, useEffect, useState } from 'react';

import { api, AxiosError } from '@/services/biometricApi';
import { RekognitionSessionResponse } from '@/model/RekognitionSessionResponse';
import { RekognitionSessionCreateResponse } from '@/model/RekognitionSessionCreateResponse';
import { PostMessageRequest } from '@/model/PostMessageRequest';
import { LivenessError } from '@/model/LivenessError';
import { LivenessErrorContent } from '@/model/LivenessErrorContent';
import { CustomError } from '@/model/CustomError';
import { BiometricAuthResponse } from '@/model/BiometricAuthResponse';
import { BiometricAuthRequest } from '@/model/BiometricAuthRequest';
import { FaceContainer } from './ui';

interface StateProps {
  request: PostMessageRequest;
}

interface DispatchProps {
  onAnalysisComplete: (data: RekognitionSessionResponse) => void;
}

type Props = StateProps & DispatchProps;

export const FaceScreen: React.FC<Props> = ({ request, onAnalysisComplete }): JSX.Element => {
  const [biometricToken, setBiometricToken] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [sessionId, setSessionId] = useState<string>();
  const [livenessErrorContent, setLivenessErrorContent] = useState<LivenessErrorContent>();
  const [isAnalysisComplete, setIsAnalysisComplete] = useState<boolean>(false);
  const [screen, setScreen] = useState<number>(0);

  const authBiometric = async (): Promise<void> => {
    const payload: BiometricAuthRequest = {
      company: request.company,
      personIdentification: request.personIdentification,
      origin: request.origin,
      systemOS: request.systemOS,
      browser: request.browser as string,
    };
    const headers = {
      'Content-Type': 'application/json',
      'Api-Key': request.apiKey,
    };
    const { data } = await api.post<BiometricAuthResponse>('/auth/v1', payload, {
      headers,
    });
    setBiometricToken(data.token);
  };

  const fetchCreateLiveness = async (token: string): Promise<void> => {
    try {
      const { data } = await api.get<RekognitionSessionCreateResponse>('/rekognition/v1', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setSessionId(data.sessionId);
      setLoading(false);
    } catch (error) {
      if (error instanceof AxiosError) {
        let message = '';
        const axiosError = error as AxiosError<CustomError, CustomError>;

        if (axiosError.response?.status === 401) {
          message = 'Sua sessão expirou. Por favor, tente novamente.';
        } else if (axiosError.response && axiosError.response.data) {
          if (axiosError.response.data.details && axiosError.response.data.details.length > 0) {
            // eslint-disable-next-line no-plusplus
            for (let i = 0; i < axiosError.response.data.details.length; i++) {
              if (i > 0) {
                break;
              } else {
                message = axiosError.response.data.details[i];
              }
            }
          } else if (axiosError.response.data.message) {
            message = axiosError.response.data.message;
          }
        }
        if (!message) {
          message = 'Ocorreu um erro ao obter a sessão. Por favor, tente novamente.';
        }
        const newLivenessErrorContent: LivenessErrorContent = {
          title: 'Não foi possível confirmar sua Biometria Facial',
          message,
        };
        setSessionId(undefined);
        setLoading(false);
        setIsAnalysisComplete(false);
        setLivenessErrorContent(newLivenessErrorContent);
        setScreen(2);
      } else {
        setSessionId(undefined);
        setLoading(false);
        setIsAnalysisComplete(false);
        const newLivenessErrorContent: LivenessErrorContent = {
          title: 'Erro ao obter sessão',
          message:
            'Sua sessão expirou ou ocorreu um erro ao obter a sessão. Por favor, tente novamente.',
        };
        setLivenessErrorContent(newLivenessErrorContent);
        setScreen(2);
      }
    }
  };

  const nextScreen = async (): Promise<void> => {
    const newScreen = screen + 1;
    if (newScreen === 1 && biometricToken) {
      setScreen(newScreen);
      if (!sessionId) {
        fetchCreateLiveness(biometricToken);
      }
    }
  };

  const handleOnAnalysisComplete = async (): Promise<void> => {
    setIsAnalysisComplete(true);
  };

  const handleOnFetchSessionResult = async (): Promise<void> => {
    try {
      setLoading(true);
      const { data } = await api.get<RekognitionSessionResponse>(`/rekognition/v1/${sessionId}`, {
        headers: {
          Authorization: `Bearer ${biometricToken}`,
        },
      });

      if (data && data.confidence && data.confidence > 90) {
        setScreen(3);
        setTimeout(() => {
          onAnalysisComplete(data);
        }, 2000);
      } else {
        setSessionId(undefined);
        setLoading(false);
        setIsAnalysisComplete(false);
        const newLivenessErrorContent: LivenessErrorContent = {
          title: 'Não foi possível confirmar sua Biometria Facial',
          message: 'Sua pontuação biométrica é inferior a 90%. Por favor, tente novamente.',
        };
        setLivenessErrorContent(newLivenessErrorContent);
        setScreen(2);
      }
    } catch (error) {
      setSessionId(undefined);
      setLoading(false);
      setIsAnalysisComplete(false);
      let message = 'Ocorreu um erro ao obter o resultado da sessão. Por favor, tente novamente.';
      if (error instanceof AxiosError) {
        const axiosError = error as AxiosError<CustomError, CustomError>;
        if (axiosError.response?.status === 401) {
          message = 'Sua sessão expirou. Por favor, tente novamente.';
        } else if (axiosError.response && axiosError.response.data) {
          if (axiosError.response.data.details && axiosError.response.data.details.length > 0) {
            const [firstDetail] = axiosError.response.data.details;
            message = firstDetail;
          } else if (axiosError.response.data.message) {
            message = axiosError.response.data.message;
          }
        }
      }
      const newLivenessErrorContent: LivenessErrorContent = {
        title: 'Não foi possível confirmar sua Biometria Facial',
        message,
      };
      setLivenessErrorContent(newLivenessErrorContent);
      setScreen(2);
    }
  };

  const handleOnError = async (livenessError: LivenessError): Promise<void> => {
    const newLivenessErrorContent: LivenessErrorContent = {
      title: 'Não foi possível confirmar sua Biometria Facial',
      message: '',
    };
    if (livenessError.state === 'CONNECTION_TIMEOUT') {
      newLivenessErrorContent.message =
        'Sua sessão expirou ou o tempo limite de conexão foi atingido. Por favor, tente novamente.';
    } else if (livenessError.state === 'TIMEOUT') {
      newLivenessErrorContent.message =
        'Sua sessão expirou ou o tempo limite foi atingido. Por favor, tente novamente.';
    } else if (livenessError.state === 'RUNTIME_ERROR') {
      newLivenessErrorContent.message =
        'Não foi possível desenhar um oval. Por favor, tente novamente.';
    } else if (livenessError.state === 'FRESHNESS_TIMEOUT') {
      newLivenessErrorContent.message =
        'Sua sessão expirou ou o tempo alocado para validação foi excedido. Por favor, tente novamente.';
    } else if (livenessError.state === 'SERVER_ERROR') {
      newLivenessErrorContent.message =
        livenessError.error?.message || 'Ocorreu um erro no servidor. Por favor, tente novamente.';
    } else if (livenessError.state === 'CAMERA_FRAMERATE_ERROR') {
      newLivenessErrorContent.message =
        'O fluxo de vídeo da câmera não atende ao número mínimo de quadros por segundo (FPS) exigido. Por favor, tente novamente.';
    } else if (livenessError.state === 'CAMERA_ACCESS_ERROR') {
      newLivenessErrorContent.message =
        'O aplicativo não consegue acessar a câmera do dispositivo. Verifique as permissões e tente novamente.';
    } else if (livenessError.state === 'FACE_DISTANCE_ERROR') {
      newLivenessErrorContent.message =
        'O rosto se encontra muito próximo da câmera. Posicione-se corretamente e tente novamente.';
    } else if (livenessError.state === 'MOBILE_LANDSCAPE_ERROR') {
      newLivenessErrorContent.message =
        'A verificação falhou porque o dispositivo estava em modo paisagem. Coloque-o na vertical e tente novamente.';
    } else if (livenessError.state === 'MULTIPLE_FACES_ERROR') {
      newLivenessErrorContent.message =
        'Foi identificado mais de um rosto na câmera. Garanta que apenas você esteja visível e tente novamente.';
    } else {
      newLivenessErrorContent.message =
        'Sua sessão expirou ou ocorreu um erro inesperado. Por favor, tente novamente.';
    }
    setSessionId(undefined);
    setLoading(false);
    setIsAnalysisComplete(false);
    setLivenessErrorContent(newLivenessErrorContent);
    setScreen(2);
    /*
    setLoading(true);
    if (postMessageRequest) {
      await authBiometric(postMessageRequest);
      setLoading(false);
      setScreen(0);
    }
    */
  };

  const backScreen = (): void => {
    if (screen > 0) {
      setSessionId(undefined);
      setScreen(0);
    }
  };

  useEffect(() => {
    if (isAnalysisComplete) {
      handleOnFetchSessionResult();
    }
  }, [isAnalysisComplete]);

  useEffect(() => {
    if (request) {
      authBiometric();
    }
  }, [request]);

  return (
    <div>
      <FaceContainer
        loading={loading}
        sessionId={sessionId}
        onAnalysisComplete={handleOnAnalysisComplete}
        onError={handleOnError}
        screen={screen}
        livenessErrorContent={livenessErrorContent}
        nextScreen={nextScreen}
        backScreen={backScreen}
        postMessageRequest={request}
      />
    </div>
  );
};
